import { useTranslation } from "react-i18next";
import { IconChevronRight, IconX } from "@tabler/icons-react";
import { Avatar } from "../../ui/Avatar";
import { cn } from "../../../lib/utils";
import { RoomSupportedByChip } from "../../room/RoomSupportedByChip";
import { useTrackOnMount } from "../../../hooks/use-track-on-mount";
import type { SuggestedRoom } from "../../../domains/gpt/transcript";

/**
 * The agent's Room proposal: the space that follows the subject the
 * conversation revealed a durable interest in.
 *
 * ONE room, never a list (PRD AC-A1c). Two rooms turned a proposal into a
 * directory, and handed the user a choice between partner spaces at the exact
 * moment we are asking them to trust the answer. The server ranks — eligibility,
 * then an exact country match, then match strength, then a stable id — so this
 * card renders a decision, not a menu.
 *
 * Rendered in the COMPOSER BAND, in place of the chat input — the same slot the
 * interest card uses, so a proposal always reads as the turn's pending question
 * rather than a card buried in the scrollback. It carries no horizontal padding
 * of its own: the band owns the inset.
 *
 * Tapping OPENS the room; it does not join it. The room's entry flow owns the
 * explanation and the contact-consent step, and a card in a chat must never
 * walk a user past either — hence a chevron, not a "Join" button. The
 * supported-by chip states the partner relationship BEFORE the tap, in the same
 * wording as the room header and the join sheet.
 *
 * TWO REGISTERS (AC-B4b). For a non-member this is a discovery: the partner
 * relationship is disclosed up front, and the tap lands on the room's entry
 * flow. For a MEMBER it is a return — no trust chip, no join framing, and the
 * tap goes straight in, because the explanation and the identity gate already
 * ran when they joined. Same trigger, same frequency policy, different words.
 *
 * Presentational on purpose: it knows nothing about the API or the router.
 */
export function RoomSuggestionCard({
  room,
  onShown,
  onOpen,
  onDismiss,
}: {
  room: SuggestedRoom;
  // The impression: fired once when this room's card reaches the screen. The
  // card reports it and does not interpret it — the hook decides what an
  // impression means (an analytics event, and a row the frequency policy reads).
  onShown: () => void;
  onOpen: () => void;
  onDismiss: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  useTrackOnMount(
    "gpt-room-suggestion-shown",
    { status: "shown", key: room.id },
    onShown
  );

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-4 rounded-2xl p-4",
        "border border-solid border-ds-slate-100",
        "bg-[rgba(253,253,253,0.9)] backdrop-blur-[3px]",
        "shadow-[0px_14px_18px_rgba(13,20,18,0.16)]"
      )}
    >
      <div className="flex w-full items-center gap-2.5">
        <p className="min-w-0 flex-1 font-dm-sans font-semibold text-[17px]/[23px] text-ds-slate-900">
          {room.isMember
            ? t("This subject is covered in your {{room}} Room", {
                room: room.name,
              })
            : t("A Juisci Room follows this subject")}
        </p>
        <button
          type="button"
          aria-label={t("Close")}
          onClick={onDismiss}
          className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full bg-[rgba(11,17,16,0.14)] text-ds-slate-800"
        >
          <IconX size={15} stroke={2.5} />
        </button>
      </div>

      <button
        type="button"
        onClick={onOpen}
        className={cn(
          "flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-left",
          "border border-solid border-transparent bg-[rgba(11,17,16,0.04)]",
          "transition-colors duration-150 ease-out"
        )}
      >
        {room.imageUrl === null ? (
          <Avatar
            variant="initials"
            initials={getInitials(room.name)}
            size="md"
            badge={null}
          />
        ) : (
          <Avatar
            variant="image"
            src={room.imageUrl}
            alt={room.name}
            imageFit="scale-down"
            size="md"
            badge={null}
          />
        )}
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="truncate font-dm-sans text-[15px]/5 font-medium tracking-[-0.15px] text-ds-mint-900">
            {room.name}
          </span>
          {room.shortDescription !== null && (
            <span className="line-clamp-2 font-dm-sans text-footnote text-ds-moss-600">
              {room.shortDescription}
            </span>
          )}
          {/* A member has already read the partner relationship — at sign-up,
              in the entry sheet, and on the room header. Repeating it on a
              "come back to your own space" card would re-frame a return as a
              fresh commercial proposal, which is the opposite of the register
              AC-B4b asks for. Non-members see it BEFORE the tap, always. */}
          {room.isMember ? (
            <span className="font-dm-sans text-footnote font-medium text-ds-mint-900">
              {t("Back to the Room ›")}
            </span>
          ) : (
            <span className="flex">
              <RoomSupportedByChip
                organisationName={room.organisationName}
                size="sm"
              />
            </span>
          )}
        </span>
        <IconChevronRight
          size={18}
          stroke={2}
          className="shrink-0 text-ds-moss-600"
        />
      </button>
    </div>
  );
}

// Avatar fallback when a room has neither an icon nor an organisation logo.
// Local on purpose: the Rooms Center has its own copy, and two call sites do
// not make a shared helper — they make a shared file nobody owns.
function getInitials(name: string): string {
  return name
    .split(/[\s&]+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .slice(0, 3)
    .toUpperCase();
}
